import React from 'react';
import { Segmented } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../assets/utils/usePermissions';
import { LEGACY_MODULE_ROLE_IDS } from '../assets/utils/accessCatalog';
import useClaimsRollout from '../hooks/useClaimsRollout';

/**
 * In-module navigation. Functions that belong to a module (Facility types
 * inside Facilities, Announcements inside Campaigns, …) are reached from the
 * module's own page through this strip, not from the sidebar: the main
 * navigation stays one item per module. Each entry is gated by its own access
 * module, exactly as a sidebar item would be, and the strip hides itself when
 * fewer than two entries are visible.
 */
export const MODULE_NAVS = {
  facilities: [
    { label: 'Directory', path: '/facilities', module: 'facilities' },
    { label: 'Facility types', path: '/admin/settings/facility-types', module: 'facilityTypes' },
    { label: 'Facility ratings', path: '/admin/facility-ratings', module: 'facilityRatings' },
    { label: 'Portal help & tutorials', path: '/admin/help-content', module: 'helpContent' },
  ],
  encounters: [
    { label: 'Encounters', path: '/checkins', module: 'encounters' },
    { label: 'Soft-deleted check-ins', path: '/admin/soft-deleted-checkins', module: 'checkinCleanup' },
    { label: 'Facility summary', path: '/checkins/facility-summary', module: 'encounters' },
  ],
  campaigns: [
    { label: 'Push notifications', path: '/admin/campaigns?channel=push', module: 'broadcastNotifications' },
    { label: 'Email notifications', path: '/admin/campaigns?channel=email', module: 'broadcastNotifications' },
    { label: 'Announcements', path: '/admin/announcements', module: 'announcements' },
    { label: 'Facility notices', path: '/admin/facility-notices', module: 'facilityNotices' },
  ],
  enrollees: [
    { label: 'Enrollees dashboard', path: '/dashboard/enrollees', module: 'dashboard' },
    { label: 'Form settings', path: '/admin/form-settings', module: 'formSettings' },
  ],
  // Pending submission (spec §8) belongs to the new encounter/claims flow, so
  // its entry appears only once the API reports that flow switched on.
  claims: [
    { label: 'Claims processing', path: '/claims', module: 'claims' },
    {
      label: 'Pending submission',
      path: '/claims/pending-submissions',
      module: 'claims',
      rolloutFlag: 'submit_separation_enabled',
    },
    // System-config admins only; the place the switches above are turned on.
    { label: 'Rollout switches', path: '/admin/settings/claims-rollout', module: 'claimsRollout' },
  ],
};

const SUPER_ONLY = [1];

export default function ModuleNav({ items, style }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { canStayOnModule } = usePermissions();
  const rollout = useClaimsRollout();

  const visible = (items || []).filter((item) => (
    (!item.module || canStayOnModule(item.module, LEGACY_MODULE_ROLE_IDS[item.module] || SUPER_ONLY))
    // Entries tied to a rollout switch stay hidden until the server reports it on.
    && (!item.rolloutFlag || Boolean(rollout[item.rolloutFlag]))
  ));
  if (visible.length < 2) return null;

  const search = (location.search || '').replace(/^\?/, '');
  const isActive = (item) => {
    const [pathname, query] = String(item.path).split('?');
    if (location.pathname !== pathname) return false;
    return query === undefined ? true : search === query;
  };
  const current = visible.find(isActive)?.path;

  return (
    <Segmented
      className="module-nav"
      style={{ marginBottom: 12, ...style }}
      value={current}
      onChange={(path) => { if (path !== current) navigate(path); }}
      options={visible.map((item) => ({ label: item.label, value: item.path }))}
    />
  );
}
